import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  EnvelopeIcon,
  CalendarIcon,
  ShieldCheckIcon,
  GearIcon,
  PencilIcon,
  LightbulbFilamentIcon,
} from '@phosphor-icons/react';
import { supabase } from '@/lib/supabase';
import { AccountContent } from '@/content/AccountContent';
import { useUserProfile } from '@/lib/hooks/useUserProfile';
import { useRoleDirectory } from '@/lib/hooks/useRoleDirectory';
import { getInitialBadgeColor } from '@/lib/utils/color.utils';
import { getInitials } from '@/lib/utils/name.utils';
import { getGroupRoleTone } from '@/lib/utils/groups.utils';
import { getRoleLabelByKey } from '@/lib/utils/roles.utils';
import { cn } from '@/lib/utils';
import { VersionInfo } from '@/components/VersionInfo';
import { Button } from '@/components/ui/button';
import { ActionCard } from '@/components/ui/ActionCard';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
  DrawerClose,
} from '@/components/ui/drawer';

/**
 * Format member since date for display
 * Example: "Dec 12, 2023"
 */
function formatMemberSinceDate(createdAt: string): string {
  const date = new Date(createdAt);
  const month = date.toLocaleDateString('en-US', { month: 'short' });
  const day = date.getDate();
  const year = date.getFullYear();

  return `${month} ${day}, ${year}`;
}

export function Account() {
  const { profile, isLoading: isProfileLoading } = useUserProfile();
  useRoleDirectory();
  const navigate = useNavigate();
  const [isSignOutDrawerOpen, setIsSignOutDrawerOpen] = useState(false);

  // Handle sign out
  // Navigation to /login is handled by main.tsx after auth state updates
  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Error signing out:', error);
      }
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const userName =
    profile?.full_name || profile?.email || AccountContent.profile.unknownName;
  const userInitials = getInitials(userName);
  const userBadgeColor = getInitialBadgeColor(userName);
  const roleTone = getGroupRoleTone(profile?.role);

  return (
    <div className="w-full max-w-4xl mx-auto px-4 pb-8 mt-[34px]">
      <h1 className="font-field-74 text-h1 text-slate-50 mb-6">
        {AccountContent.title}
      </h1>

      {/* Profile Section */}
      <div className="bg-slate-600 rounded-xl p-6 mb-8">
        {isProfileLoading ? (
          <div className="animate-pulse space-y-4">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-lg bg-slate-500" />
              <div className="h-6 w-32 rounded bg-slate-500" />
            </div>
            <div className="space-y-3">
              <div className="h-4 w-48 rounded bg-slate-500" />
              <div className="h-4 w-36 rounded bg-slate-500" />
              <div className="h-4 w-40 rounded bg-slate-500" />
            </div>
          </div>
        ) : (
          <>
            {/* Name with avatar and Edit button */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <span
                  className={`flex h-12 w-12 items-center justify-center rounded-lg text-large text-slate-50 ${userBadgeColor}`}
                >
                  {userInitials}
                </span>
                <span className="text-h2 text-slate-50 font-field-64">
                  {userName}
                </span>
              </div>
              <Button
                variant="link"
                size="sm"
                onClick={() => navigate({ to: '/account/edit' })}
                className="flex items-center gap-2 text-slate-200"
              >
                <PencilIcon size={16} weight="regular" />
                Edit
              </Button>
            </div>

            {/* Profile details */}
            <div className="space-y-3">
              {/* Email */}
              {profile?.email && (
                <div className="flex items-center gap-3 text-body text-slate-300">
                  <EnvelopeIcon
                    size={20}
                    weight="regular"
                    className="text-slate-400"
                  />
                  <span>{profile.email}</span>
                </div>
              )}

              {/* Role */}
              <div className="flex items-center gap-3 text-body text-slate-300">
                <ShieldCheckIcon
                  size={20}
                  weight="regular"
                  className="text-slate-400"
                />
                <span
                  className={cn(
                    'inline-flex rounded-full border px-2 py-0.5 font-field-42 text-[10px] uppercase tracking-wide',
                    roleTone.pillClassName
                  )}
                >
                  {getRoleLabelByKey(profile?.role)}
                </span>
              </div>

              {/* Member since */}
              {profile?.created_at && (
                <div className="flex items-center gap-3 text-body text-slate-300">
                  <CalendarIcon
                    size={20}
                    weight="regular"
                    className="text-slate-400"
                  />
                  <span>
                    Member since {formatMemberSinceDate(profile.created_at)}
                  </span>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <ActionCard
        hasBadge
        className="mb-4"
        title="Settings"
        icon={GearIcon}
        badgeColor="sky"
        onClickAction={() => navigate({ to: '/account/settings' })}
      />

      <ActionCard
        hasBadge
        className="mb-4"
        title={AccountContent.feedback.title}
        subtext={AccountContent.feedback.description}
        icon={LightbulbFilamentIcon}
        badgeColor="purple"
        onClickAction={() => navigate({ to: '/account/feedback' })}
      />

      <Button
        variant="destructive"
        size="lg"
        className="w-full mt-6"
        onClick={() => setIsSignOutDrawerOpen(true)}
      >
        {AccountContent.signOut.label}
      </Button>

      {/* Sign Out Confirmation Drawer */}
      <Drawer open={isSignOutDrawerOpen} onOpenChange={setIsSignOutDrawerOpen}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>
              {AccountContent.signOut.confirmation.title}
            </DrawerTitle>
            <DrawerDescription>
              {AccountContent.signOut.confirmation.description}
            </DrawerDescription>
          </DrawerHeader>
          <DrawerFooter>
            <Button variant="destructive" size="lg" onClick={handleSignOut}>
              {AccountContent.signOut.confirmation.confirm}
            </Button>
            <DrawerClose asChild>
              <Button variant="outline" size="lg">
                {AccountContent.signOut.confirmation.cancel}
              </Button>
            </DrawerClose>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      <VersionInfo />
    </div>
  );
}
